import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { isInstanceCreated } from '../../../apis/constants';
import { instanceApis } from '../../../apis/instanceApis';
import { solrApis } from '../../../apis/solrApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import Storage from '../../../components/elements/storage/Storage';
import Text from '../../../components/elements/text/Text';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import List from '../../../components/sections/list/List';
import InstanceInfo from '../../../components/sections/overview/InstanceInfo';
import Overview from '../../../components/sections/overview/Overview';
import SolrButtons from '../../../components/sections/overview/SolrButtons';
import { useSolrInstance } from '../../../contexts/SolrContext';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { isSolrCloud } from '../../../utils/utils';

const SolrOverviewPage = () => {
  const { t } = useTranslation();
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();
  const { instance, loading, refreshSolrStatus } = useSolrInstance();

  const [storage, setStorage] = useState(null);
  const [solrProcessing, setSolrProcessing] = useState(false);

  const handleFetchStorage = async () => {
    if (isInstanceCreated(instance.status)) {
      const { data: storageRes } = await instanceApis.getStorage(
        projectId,
        instanceId
      );
      setStorage(storageRes);
    }
  };

  useEffect(() => {
    if (!instance) return;
    handleFetchStorage();
  }, [instance]);

  const handleStartInstance = async () => {
    setSolrProcessing(true);
    try {
      await solrApis.startSolr(projectId, instanceId);
      await refreshSolrStatus();
      showToast(
        TOAST_TYPE.SUCCESS,
        t('solrStartSuccess', { ns: 'notifications' })
      );
    } catch (error) {
      showToast(TOAST_TYPE.ERROR, t('solrStartError', { ns: 'notifications' }));
    } finally {
      setSolrProcessing(false);
    }
  };

  const handleStopInstance = async () => {
    setSolrProcessing(true);
    try {
      await solrApis.stopSolr(projectId, instanceId);
      await refreshSolrStatus();
      showToast(
        TOAST_TYPE.SUCCESS,
        t('solrStopSuccess', { ns: 'notifications' })
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.SUCCESS,
        t('solrStopError', { ns: 'notifications' })
      );
    } finally {
      setSolrProcessing(false);
    }
  };

  const handleRestartInstance = async () => {
    setSolrProcessing(true);
    try {
      // manually change instance status while reloading
      instance.instances[0].status = false;

      await solrApis.restartSolr(projectId, instanceId);
      await refreshSolrStatus();
      showToast(
        TOAST_TYPE.SUCCESS,
        t('solrRestartSuccess', { ns: 'notifications' })
      );
    } catch (error) {
      await refreshSolrStatus();
      showToast(
        TOAST_TYPE.ERROR,
        t('solrRestartError', { ns: 'notifications' })
      );
    } finally {
      setSolrProcessing(false);
    }
  };

  const handleStartNode = async (subscriptNum) => {
    try {
      await solrApis.startSolrNode(projectId, instanceId, subscriptNum);
      await refreshSolrStatus();
      showToast(
        TOAST_TYPE.SUCCESS,
        t('solrNodeStartSuccess', { ns: 'notifications' })
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        t('solrNodeStartError', { ns: 'notifications' })
      );
    }
  };

  const handleStopNode = async (subscriptNum) => {
    try {
      await solrApis.stopSolrNode(projectId, instanceId, subscriptNum);
      await refreshSolrStatus();
      showToast(
        TOAST_TYPE.SUCCESS,
        t('solrNodeStopSuccess', { ns: 'notifications' })
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        t('solrNodeStopError', { ns: 'notifications' })
      );
    }
  };

  if (loading) return <Loading />;

  if (!instance)
    return (
      <AlertMessage
        severity="error"
        message={t('fetch', {
          ns: 'errors',
          content: t('instance', { ns: 'instance' }),
        })}
      />
    );

  const buttons = (
    <SolrButtons
      instance={instance}
      solrProcessing={solrProcessing}
      onStartInstance={handleStartInstance}
      onStopInstance={handleStopInstance}
      onRestartInstance={handleRestartInstance}
    />
  );

  return (
    <Container>
      <Breadcrumbs instanceName={instance.name} />
      <Overview data={instance} button={buttons} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <InstanceInfo instance={instance} />
        {storage && (
          <Storage
            available={Number(storage.available)}
            total={Number(storage.size)}
          />
        )}
      </Box>
      {isSolrCloud(instance.type) && (
        <Box>
          <Text subtitle>Solr Nodes</Text>
          <List
            isSolrNodeList
            instance={instance}
            data={instance.instances}
            onStartNode={handleStartNode}
            onStopNode={handleStopNode}
          />
        </Box>
      )}
    </Container>
  );
};

export default SolrOverviewPage;
